/**
 * Iter 127 — Data Import Wizard (multi-module: Employee first; Clients/Contacts/Projects/Brands plug in later).
 *
 * 5 steps: choose module → upload → column mapping → preview & fix →
 * commit with per-row/bulk action + role.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Upload, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";

const STEPS = ["module", "upload", "map", "preview", "commit"];

export default function DataImport() {
  const { activeWorkspaceId } = useAuth();
  const [step, setStep] = useState("module");
  const [specs, setSpecs] = useState([]);
  const [searchParams] = useSearchParams();
  const [specKey, setSpecKey] = useState(searchParams.get("entity") || "employees");
  useEffect(() => {
    const e = searchParams.get("entity");
    if (e && e !== specKey) setSpecKey(e);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [session, setSession] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rowActions, setRowActions] = useState({}); // {idx: {action, role, create_login, overrides}}
  const [defaultRole, setDefaultRole] = useState("member");
  const [defaultAction, setDefaultAction] = useState("create");
  const [dedupeStrategy, setDedupeStrategy] = useState("update"); // R138 · leads only
  const [busy, setBusy] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [enrichPreview, setEnrichPreview] = useState(null); // { total_imported, eligible_count, credits_estimated, sample }
  const [enrichConfirm, setEnrichConfirm] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  useEffect(() => { api.get("/import-specs").then(({ data }) => setSpecs(data || [])); }, []);
  const spec = useMemo(() => specs.find((s) => s.key === specKey), [specs, specKey]);

  const goto = (s) => setStep(s);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/imports/${specKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data);
      goto("map");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };

  const saveMapping = async (mapping) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/imports/${session.id}/mapping`, mapping);
      setSession(data);
      const p = await api.get(`/imports/${session.id}/preview`);
      setPreview(p.data);
      goto("preview");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      // Merge default action/role for any row without an explicit setting.
      const actions = {};
      (preview?.rows || []).forEach((r, i) => {
        const explicit = rowActions[i] || {};
        actions[i] = {
          action: explicit.action || defaultAction,
          role: explicit.role || defaultRole,
          create_login: explicit.create_login ?? true,
          overrides: explicit.overrides || {},
        };
      });
      const { data } = await api.post(`/imports/${session.id}/commit`, {
        row_actions: actions,
        dedupe_strategy: dedupeStrategy,
      });
      // R138c · Async path — backend queued the commit and returned early.
      // Poll session status until the job completes so the wizard renders
      // a live progress bar instead of hanging or timing out on the proxy.
      if (data && data.async) {
        goto("commit");
        setCommitResult({ committed: 0, errors: [], rows: [], async: true,
                            total: data.total || 0 });
        toast.info(`Import running in background · ${data.total || "?"} rows`);
        const poll = async () => {
          try {
            const { data: s } = await api.get(`/imports/${session.id}`);
            const prog = s.commit_progress || {};
            const cr = s.commit_result;
            setCommitResult((prev) => ({
              ...(prev || {}),
              async: s.status !== "committed" && s.status !== "commit_failed",
              total: prog.total || prev?.total || 0,
              done: prog.done || 0,
              running_committed: prog.committed || 0,
              running_errors: prog.errors || 0,
              committed: cr ? (cr.committed || []).length : (prog.committed || 0),
              errors: cr ? (cr.errors || []) : [],
              rows: cr ? cr.committed : [],
              status: s.status,
              commit_error: s.commit_error,
            }));
            if (s.status === "committed") {
              toast.success(`Import complete · ${(cr?.committed || []).length} rows`);
              return;
            }
            if (s.status === "commit_failed") {
              toast.error(`Import failed · ${s.commit_error || ""}`);
              return;
            }
            setTimeout(poll, 2500);
          } catch { setTimeout(poll, 4000); }
        };
        poll();
      } else {
        setCommitResult(data);
        goto("commit");
        toast.success(`Committed ${data.committed} rows${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      }
      // For prospects imports, offer to enrich rows missing linkedin/phone.
      if (specKey === "prospects" && data.committed > 0) {
        try {
          const { data: ep } = await api.get(`/imports/${session.id}/enrich-prospects/preview`);
          if (ep && ep.eligible_count > 0) setEnrichPreview(ep);
        } catch { /* Apollo not configured / other — silently skip */ }
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Commit failed")); }
    finally { setBusy(false); }
  };

  const setRowField = (i, patch) => setRowActions((prev) => ({ ...prev, [i]: { ...(prev[i] || {}), ...patch } }));

  return (
    <div className="space-y-6" data-testid="data-import-page">
      <PageHeader
        breadcrumb="Data"
        title="Import wizard"
        description="One place to bring in Employees, Clients, Contacts, Projects and Brands. Upload → map → preview → commit. PII fields are masked in preview and Aadhaar is encrypted at rest."
        actions={
          specKey ? (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/workspaces/${activeWorkspaceId}/imports/${specKey}/sample.xlsx`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 border px-3 h-9 text-xs hover:bg-neutral-50"
              data-testid="import-download-sample-btn"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const token = localStorage.getItem("co_token");
                  const r = await fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } });
                  if (!r.ok) throw new Error(`No sample for ${specKey}`);
                  const blob = await r.blob();
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u; a.download = `acquilix-${specKey}-sample.xlsx`; a.click();
                  URL.revokeObjectURL(u);
                } catch (err) {
                  toast.error(err.message || "Sample not available for this entity");
                }
              }}
            >Download sample XLSX</a>
          ) : null
        }
      />
      <Stepper current={step} />

      {step === "module" && (
        <div className="border border-border p-4" data-testid="import-step-module">
          <p className="text-sm text-muted-foreground mb-3">Pick what you&apos;re importing.</p>
          <div className="grid md:grid-cols-3 gap-3">
            {specs.map((s) => (
              <button key={s.key} onClick={() => setSpecKey(s.key)}
                className={`border p-4 text-left ${specKey === s.key ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/20"}`}
                data-testid={`spec-${s.key}`}>
                <div className="text-sm font-semibold">{s.label}</div>
                <div className="text-[11px] text-muted-foreground">{s.fields.length} fields · upsert by {s.upsert_key}</div>
              </button>
            ))}
          </div>
          <div className="mt-4"><Button className="rounded-none" onClick={() => goto("upload")} data-testid="import-next-upload">Next: upload<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></div>
        </div>
      )}

      {step === "upload" && (
        <div className="border border-border p-6 text-center" data-testid="import-step-upload">
          <FileSpreadsheet className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm mb-3">Drop or select your .xlsx / .csv file (max 15 MB).</p>
          <input type="file" accept=".xlsx,.csv" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="text-sm" data-testid="import-file-input" />
          {busy && <div className="mt-3 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin inline" /> parsing…</div>}
          <div className="mt-4"><Button variant="ghost" onClick={() => goto("module")}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Button></div>
        </div>
      )}

      {step === "map" && session && spec && (
        <MappingStep session={session} spec={spec} onSave={saveMapping} onBack={() => goto("upload")} busy={busy} />
      )}

      {step === "preview" && preview && spec && (
        <div className="space-y-3" data-testid="import-step-preview">
          <div className="border border-border p-3 bg-muted/20 flex flex-wrap items-center gap-4 text-xs">
            <div><b>{preview.total_rows}</b> total</div>
            <div className="text-green-700">✓ <b>{preview.counts.ok}</b> clean</div>
            <div className="text-amber-700"><AlertTriangle className="w-3.5 h-3.5 inline" /> <b>{preview.counts.with_errors}</b> with errors</div>
            <div className="flex-1" />
            <Label className="text-[10px] uppercase tracking-widest">Default action</Label>
            <select value={defaultAction} onChange={(e) => setDefaultAction(e.target.value)} className="h-8 rounded-none border border-border text-xs px-1 bg-background" data-testid="import-default-action">
              <option value="create">Create/update</option>
              <option value="skip">Skip</option>
            </select>
            <Label className="text-[10px] uppercase tracking-widest">Default role</Label>
            <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} className="h-8 rounded-none border border-border text-xs px-1 bg-background" data-testid="import-default-role">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
            {specKey === "leads" && (
              <>
                <Label className="text-[10px] uppercase tracking-widest">On duplicate</Label>
                <select value={dedupeStrategy}
                        onChange={(e) => setDedupeStrategy(e.target.value)}
                        className="h-8 rounded-none border border-border text-xs px-1 bg-background"
                        data-testid="import-dedupe-strategy"
                        title="What to do when an incoming lead matches an existing one (matched via email → external_id → name+phone)">
                  <option value="update">Update non-empty fields (merge)</option>
                  <option value="skip">Skip — keep existing</option>
                  <option value="overwrite">Overwrite — replace all fields</option>
                  <option value="create">Create anyway — allow duplicates</option>
                </select>
              </>
            )}
          </div>
          <PreviewTable rows={preview.rows} spec={spec} rowActions={rowActions} setRowField={setRowField} />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => goto("map")}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to mapping</Button>
            <div className="flex-1" />
            <Button onClick={commit} disabled={busy} className="rounded-none" data-testid="import-commit-btn">
              {busy ? "Committing…" : `Commit ${preview.total_rows} rows`}
            </Button>
          </div>
        </div>
      )}

      {step === "commit" && commitResult && (
        <div className="border border-border p-6" data-testid="import-step-done">
          {commitResult.async ? (
            // R138c · Feb 2026 — Async import in progress
            <>
              <Loader2 className="w-8 h-8 text-primary mb-2 animate-spin" />
              <div className="text-lg font-semibold mb-1"
                   data-testid="import-async-title">
                Import running in background
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                {commitResult.done || 0} of {commitResult.total || "?"} rows processed
                {commitResult.running_committed ? ` · ${commitResult.running_committed} committed` : ""}
                {commitResult.running_errors ? ` · ${commitResult.running_errors} errors` : ""}
              </div>
              {commitResult.total > 0 && (
                <div className="h-2 bg-muted mb-4 overflow-hidden max-w-md">
                  <div className="h-full bg-emerald-500 transition-all"
                       style={{ width: `${Math.min(100, ((commitResult.done || 0) / commitResult.total) * 100)}%` }}
                       data-testid="import-async-progress" />
                </div>
              )}
              <div className="text-[11px] text-muted-foreground max-w-md">
                Big imports run server-side to sidestep the 120 s browser
                timeout. You can safely close this tab — the job keeps
                running and appears in <b>Import history</b> once done.
              </div>
            </>
          ) : commitResult.status === "commit_failed" ? (
            <>
              <AlertTriangle className="w-8 h-8 text-rose-600 mb-2" />
              <div className="text-lg font-semibold mb-1">Import failed</div>
              <div className="text-xs text-rose-600 mb-3">{commitResult.commit_error}</div>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-8 h-8 text-green-600 mb-2" />
              <div className="text-lg font-semibold mb-1">Import complete</div>
              <div className="text-sm text-muted-foreground mb-4">
                {commitResult.committed} rows committed
                {commitResult.errors.length > 0 && ` · ${commitResult.errors.length} rows had errors`}
              </div>
            </>
          )}

          {/* R138 · Feb 2026 — Merge review CTA for leads with matched (updated) rows */}
          {specKey === "leads" && (() => {
            const updatedCount = (commitResult.rows || []).filter((r) => r.action === "updated").length;
            if (!updatedCount) return null;
            return (
              <div className="border border-amber-500/40 bg-amber-500/5 p-4 mb-4"
                   data-testid="import-merge-cta">
                <div className="text-sm font-semibold mb-1">
                  {updatedCount} existing lead{updatedCount === 1 ? " was" : "s were"} updated
                </div>
                <div className="text-xs text-muted-foreground mb-3">
                  Review the field-by-field changes so nothing valuable was overwritten. You can revert individual fields per lead.
                </div>
                <Button size="sm" className="rounded-none"
                        onClick={() => window.open(`/app/leads/merge/${session.id}`, "_blank")}
                        data-testid="import-merge-review-btn">
                  Review {updatedCount} merge{updatedCount === 1 ? "" : "s"}
                </Button>
              </div>
            );
          })()}

          {/* Iter 140 · Auto-enrichment CTA for prospects */}
          {enrichPreview && !enrichResult && (
            <div className="border border-primary/40 bg-primary/5 p-4 mb-4" data-testid="import-enrich-cta">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold mb-1">Auto-enrich imported prospects?</div>
                  <div className="text-xs text-muted-foreground mb-3">
                    <b>{enrichPreview.eligible_count}</b> of your {enrichPreview.total_imported} imported prospects
                    are missing a LinkedIn URL or phone number. Running Apollo enrichment
                    will use approximately <b>{enrichPreview.credits_estimated} API credit{enrichPreview.credits_estimated === 1 ? "" : "s"}</b>{" "}
                    from your workspace&apos;s Apollo balance.
                  </div>
                  {enrichPreview.sample?.length > 0 && (
                    <div className="text-[11px] text-muted-foreground mb-3">
                      Sample: {enrichPreview.sample.map((s) => s.name || s.email).slice(0, 3).join(", ")}
                      {enrichPreview.eligible_count > 3 && ` and ${enrichPreview.eligible_count - 3} more…`}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-none" onClick={() => setEnrichConfirm(true)}
                            disabled={enrichBusy} data-testid="import-enrich-start-btn">
                      <Sparkles className="w-3.5 h-3.5 mr-1" /> Enrich {enrichPreview.eligible_count} prospects
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setEnrichPreview(null)}
                            data-testid="import-enrich-skip-btn">Skip</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {enrichResult && (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 mb-4 text-sm" data-testid="import-enrich-result">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 inline mr-1" />
              Enriched <b>{enrichResult.enriched}</b> of {enrichResult.eligible} prospects
              {enrichResult.failed > 0 && ` · ${enrichResult.failed} failed`}
              {enrichResult.revealed_emails > 0 && ` · ${enrichResult.revealed_emails} new email${enrichResult.revealed_emails === 1 ? "" : "s"}`}
              {enrichResult.revealed_phones > 0 && ` · ${enrichResult.revealed_phones} new phone${enrichResult.revealed_phones === 1 ? "" : "s"}`}
            </div>
          )}

          <div className="flex gap-2 mb-4">
            <Button variant="outline" className="rounded-none"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v1/imports/${session.id}/export`, "_blank")}
              data-testid="import-download-xlsx"
            >📥 Download imported rows (XLSX)</Button>
            <Button variant="outline" className="rounded-none text-red-600 hover:bg-red-50"
              onClick={async () => {
                if (!window.confirm(`Rollback this import? This will HARD-DELETE the ${commitResult.committed} rows that were just imported.`)) return;
                try {
                  const { data } = await api.post(`/imports/${session.id}/rollback`);
                  toast.success(`Rolled back ${data.rolled_back} rows`);
                  setStep("module"); setSession(null); setPreview(null); setRowActions({}); setCommitResult(null); setEnrichPreview(null); setEnrichResult(null);
                } catch (e) { toast.error(apiErrorMessage(e, "Rollback failed")); }
              }}
              data-testid="import-rollback-btn"
            >↩ Rollback this import</Button>
          </div>
          {commitResult.errors.length > 0 && (
            <ul className="border border-red-300 bg-red-50 p-3 text-xs space-y-1 max-h-64 overflow-y-auto">
              {commitResult.errors.map((e, i) => (
                <li key={i}>Row {e.row}: {e.error}</li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => { setStep("module"); setSession(null); setPreview(null); setRowActions({}); setCommitResult(null); setEnrichPreview(null); setEnrichResult(null); }} className="rounded-none">Start another import</Button>
          </div>
        </div>
      )}

      {enrichConfirm && enrichPreview && (
        <AlertDialog open onOpenChange={(v) => { if (!v) setEnrichConfirm(false); }}>
          <AlertDialogContent data-testid="import-enrich-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Apollo enrichment</AlertDialogTitle>
              <AlertDialogDescription>
                This will call Apollo <code>/people/match</code> for the {enrichPreview.eligible_count}{" "}
                prospect{enrichPreview.eligible_count === 1 ? "" : "s"} missing LinkedIn / phone,
                consuming ≈ <b>{enrichPreview.credits_estimated} credit{enrichPreview.credits_estimated === 1 ? "" : "s"}</b>{" "}
                from your workspace&apos;s Apollo balance. Any newly-revealed emails, phones or LinkedIn URLs will be saved onto each prospect.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="import-enrich-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="import-enrich-confirm-run"
                disabled={enrichBusy}
                onClick={async () => {
                  setEnrichBusy(true);
                  try {
                    const { data } = await api.post(`/imports/${session.id}/enrich-prospects`, { limit: 500 });
                    setEnrichResult(data);
                    setEnrichConfirm(false);
                    setEnrichPreview(null);
                    toast.success(`Enriched ${data.enriched} prospects`);
                  } catch (e) {
                    toast.error(apiErrorMessage(e, "Enrichment failed"));
                  } finally { setEnrichBusy(false); }
                }}
              >{enrichBusy ? "Enriching…" : `Enrich · use ${enrichPreview.credits_estimated} credits`}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-6 h-6 flex items-center justify-center border ${s === current ? "border-primary bg-primary text-primary-foreground" : STEPS.indexOf(current) > i ? "border-green-600 text-green-600" : "border-border text-muted-foreground"}`}>{i + 1}</div>
          <span className={`capitalize ${s === current ? "font-semibold" : "text-muted-foreground"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="text-muted-foreground">›</span>}
        </div>
      ))}
    </div>
  );
}

function MappingStep({ session, spec, onSave, onBack, busy }) {
  const [mapping, setMapping] = useState(session.mapping || {});
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState("");

  useEffect(() => {
    // R138 · Load saved column-mapping presets for this spec so future
    // exports from the same source load in one click.
    api.get(`/workspaces/${session.workspace_id}/mapping-presets?spec_key=${spec.key}`)
      .then(({ data }) => setPresets(data || []))
      .catch(() => { /* silent */ });
  }, [session.workspace_id, spec.key]);

  const setPair = (header, key) => setMapping((prev) => ({ ...prev, [header]: key }));
  const applyPreset = (pid) => {
    setSelectedPresetId(pid);
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    // Only apply mapping entries whose sheet header exists in this file.
    const next = {};
    session.headers.forEach((h) => {
      if (p.mapping[h]) next[h] = p.mapping[h];
    });
    setMapping(next);
    toast.success(`Loaded preset "${p.name}"`);
  };
  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Give this preset a name first"); return; }
    setSavingPreset(true);
    try {
      const { data } = await api.post(`/workspaces/${session.workspace_id}/mapping-presets`, {
        name, spec_key: spec.key, mapping,
      });
      setPresets((prev) => [data, ...prev]);
      setSelectedPresetId(data.id);
      setPresetName("");
      toast.success(`Saved "${name}"`);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingPreset(false); }
  };
  const deletePreset = async (pid) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await api.delete(`/mapping-presets/${pid}`);
      setPresets((prev) => prev.filter((p) => p.id !== pid));
      if (selectedPresetId === pid) setSelectedPresetId("");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const mapped = Object.values(mapping).filter(Boolean).length;
  const requiredMissing = spec.fields
    .filter((f) => f.required)
    .filter((f) => !Object.values(mapping).includes(f.key))
    .map((f) => f.label);
  return (
    <div className="space-y-3" data-testid="import-step-map">
      <div className="border border-border p-2 bg-muted/20 flex flex-wrap items-center gap-2 text-xs"
           data-testid="import-preset-bar">
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Presets</span>
        <select value={selectedPresetId}
                onChange={(e) => applyPreset(e.target.value)}
                className="h-7 rounded-none border border-border text-xs bg-background px-1 min-w-[220px]"
                data-testid="import-preset-select">
          <option value="">Load a saved column mapping…</option>
          {presets.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {selectedPresetId && (
          <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px] text-rose-700"
                  onClick={() => deletePreset(selectedPresetId)}
                  data-testid="import-preset-delete">Delete preset</Button>
        )}
        <div className="flex-1" />
        <Input value={presetName}
               onChange={(e) => setPresetName(e.target.value)}
               placeholder="Save this mapping as…"
               className="h-7 text-xs w-[220px]"
               data-testid="import-preset-name" />
        <Button size="sm" variant="outline" className="h-7 rounded-none text-[11px]"
                onClick={savePreset}
                disabled={savingPreset || !presetName.trim() || mapped === 0}
                data-testid="import-preset-save">
          {savingPreset ? "Saving…" : "Save preset"}
        </Button>
      </div>
      <div className="text-sm text-muted-foreground">
        Auto-mapped <b>{mapped}</b> of {session.headers.length} columns.
        {requiredMissing.length > 0 && <span className="text-red-600 ml-2">Missing required: {requiredMissing.join(", ")}</span>}
      </div>
      <div className="border border-border overflow-x-auto max-h-[60vh]">
        <table className="w-full text-xs">
          <thead className="bg-muted/30 sticky top-0"><tr>
            <th className="text-left px-3 py-2">Sheet column</th>
            <th className="text-left px-3 py-2">Sample</th>
            <th className="text-left px-3 py-2">Import as</th>
          </tr></thead>
          <tbody>
            {session.headers.map((h) => (
              <tr key={h} className="border-t border-border" data-testid={`map-row-${h}`}>
                <td className="px-3 py-1.5 font-mono">{h}</td>
                <td className="px-3 py-1.5 text-muted-foreground truncate max-w-xs">
                  {(session.rows[0] || []).find((_, idx) => session.headers[idx] === h) || "—"}
                </td>
                <td className="px-3 py-1.5">
                  <select value={mapping[h] || ""} onChange={(e) => setPair(h, e.target.value)}
                    className="h-8 rounded-none border border-border text-xs bg-background px-1 min-w-48"
                    data-testid={`map-select-${h}`}>
                    <option value="">— skip —</option>
                    {spec.fields.map((f) => <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Button>
        <div className="flex-1" />
        <Button onClick={() => onSave(mapping)} disabled={busy || requiredMissing.length > 0} className="rounded-none" data-testid="import-map-save">
          {busy ? "Preparing…" : "Preview →"}
        </Button>
      </div>
    </div>
  );
}

function PreviewTable({ rows, spec, rowActions, setRowField }) {
  const cols = spec.fields.slice(0, 8); // preview a subset for width
  return (
    <div className="border border-border overflow-x-auto max-h-[60vh]">
      <table className="w-full text-xs">
        <thead className="bg-muted/30 sticky top-0"><tr>
          <th className="px-2 py-2 text-left">Row</th>
          <th className="px-2 py-2 text-left">Action</th>
          <th className="px-2 py-2 text-left">Login?</th>
          {cols.map((c) => <th key={c.key} className="px-2 py-2 text-left">{c.label}</th>)}
          <th className="px-2 py-2 text-left">Errors</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const errs = r._meta.errors;
            const ra = rowActions[i] || {};
            return (
              <tr key={i} className={`border-t border-border ${errs.length ? "bg-red-50/50" : ""}`} data-testid={`preview-row-${i}`}>
                <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <select value={ra.action || ""} onChange={(e) => setRowField(i, { action: e.target.value })} className="h-7 rounded-none border border-border text-[10px] bg-background px-1" data-testid={`preview-action-${i}`}>
                    <option value="">(default)</option>
                    <option value="create">Create/update</option>
                    <option value="skip">Skip</option>
                  </select>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input type="checkbox" checked={ra.create_login ?? true} onChange={(e) => setRowField(i, { create_login: e.target.checked })} data-testid={`preview-login-${i}`} />
                </td>
                {cols.map((c) => {
                  const cell = r[c.key];
                  const err = cell?.error;
                  return (
                    <td key={c.key} className={`px-2 py-1.5 ${err ? "bg-red-100" : ""}`} title={err || ""}>
                      {err ? (
                        <input value={(rowActions[i]?.overrides?.[c.key]) ?? (cell?.value || "")} onChange={(e) => setRowField(i, { overrides: { ...(rowActions[i]?.overrides || {}), [c.key]: e.target.value } })} className="h-6 border border-red-300 text-[10px] px-1 w-full" data-testid={`preview-fix-${i}-${c.key}`} />
                      ) : (
                        <span className="truncate block max-w-40">{cell?.display ?? "—"}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-red-700 text-[10px]">
                  {errs.map((e, ix) => <div key={ix}>{e.field}: {e.error}</div>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
